import { isIe } from './browser_util'

declare global {
  interface Window {
    _tooltip: any;
    _deleteBtn: any;
    _addcart: any;
    _checkboxctrl: any;
    _simpleAccordion: any;
    _modal: any;
    _setitemcardheight: any;
    _ieStickyCol: any;
    _dragHorizontalScroll: any;
  }
}

class EmptyClass { } // common.js同期していない場合画面エラーならないよう

/**
 * 画面置換の処理を実行する
 * 新しいDOMの引数定義、画面置換できない場合のアクション定義、画面置換後の遷移を行う
 */
export interface ReplaceOption<T> extends ReInitOption {
  // 新しいDOMの中のEnv.PAGEのJsonが必要であれば、このCallback引数を定義する
  onJsEnvLoaded?: (newModel: T) => void;
  // 画面置換できない場合（例えばエラー画面のような返却された画面に期待通りにしない場合）のアクションを定義する
  onNoneSelectorMatch?: () => void;
  // 画面置換終わったら画面の特定場所（anchor == #id）に遷移させる
  scrollIntoAnchorSelector?: string;
}
interface ReInitOption {
  reInitAll?: boolean; // trueの場合、全てのcommon.jsを再度初期化。
  isInitTooltips?: boolean; // 個別初期化の場合、下記のisInitXxxxを独自調整可能
  isInitDeleteBtn?: boolean;
  isInitAddCart?: boolean;
  isInitModal?: boolean;
  isInitSimpleAccordion?: boolean;
  isInitCheckBoxCtrl?: boolean;
  isInitCarousel?: boolean;
  isInitItemCardHeight?: boolean;
  isInitIeStickyCol?: boolean;
  isInitDragHorizontalScroll?: boolean;
}

const initWindowTooltip = (selector: string) => {
  const WindowTooltip = window._tooltip || EmptyClass;
  return new WindowTooltip(selector);
};

const initWindowDeleteBtn = (selector: string) => {
  const WindowDeleteBtn = window._deleteBtn || EmptyClass;
  return new WindowDeleteBtn(selector);
};

const initWindowAddCart = (selector: string) => {
  const WindowAddCart = window._addcart || EmptyClass;
  return new WindowAddCart(selector);
};
const initCheckBoxCtrl = (selector: string) => {
  const WindowCheckBoxCtrl = window._checkboxctrl || EmptyClass;
  return new WindowCheckBoxCtrl(selector);
};
const initSimpleAccordion = (selector: string) => {
  const WindowSimpleAccordion = window._simpleAccordion || EmptyClass;
  return new WindowSimpleAccordion(selector);
};
const initCarousel = (selector: string) => {
  const WindowCarousel = window._carousel || EmptyClass;

  document.querySelectorAll(selector).forEach(it => {
    // see https://confluence.askul.co.jp/pages/viewpage.action?pageId=769165064
    new WindowCarousel('.js-carousel', it); // NOSONAR
  });
};
const microModalTrigger = 'data-micromodal-trigger';
const ajaxModalTrigger = 'data-ajaxmodal-trigger';
const initModal = (selector: string) => {
  const triggers = document.querySelectorAll(`${selector} [${microModalTrigger}]`);
  // 重複initModel呼び出す防止
  const randomId = ajaxModalTrigger + Date.now();
  triggers.forEach((trigger) => {
    const triggerTarget = trigger.getAttribute(microModalTrigger);
    if (triggerTarget != null) {
      trigger.setAttribute(randomId, triggerTarget);
      trigger.removeAttribute(microModalTrigger);
    }
  });
  const WindowModal = window._modal || EmptyClass;
  return new WindowModal(randomId);
};

const initItemcardheight = (selector: string) => {
  const WindowSetItemCardHeight = window._setitemcardheight || EmptyClass;
  return new WindowSetItemCardHeight(selector);
}

const initIeStickyCol = (selector: string) => {
  const IeStickyCol = window._ieStickyCol || EmptyClass;
  return new IeStickyCol(selector);
}

const initDragHorizontalScroll = (selector: string) => {
  const DragHorizontalScroll = window._dragHorizontalScroll || EmptyClass;
  return new DragHorizontalScroll(selector);
}

function initSeperatedOption(selector: string, option: ReInitOption) {
  if (option.isInitCheckBoxCtrl) {
    initCheckBoxCtrl(selector);
  }
  if (option.isInitDeleteBtn) {
    initWindowDeleteBtn(selector);
  }
  if (option.isInitAddCart) {
    initWindowAddCart(selector);
  }
  // IEのみ呼び出す
  if (option.isInitIeStickyCol && isIe) {
    initIeStickyCol(selector);
  }
  // initAddCartかinitIeStickyColは必ずinitWindowTooltipとセットして呼び出す必要がある
  // しかしinitWindowTooltipと一緒に呼び出す設定をし忘れることがあるため、自動的に呼び出すようにする
  const isAddCartOrIeStickyCol = option.isInitAddCart || option.isInitIeStickyCol;
  if ((isAddCartOrIeStickyCol && !option.isInitTooltips) ||
    option.isInitTooltips) {
    initWindowTooltip(selector);
  }
  if (option.isInitModal) {
    initModal(selector);
  }
  if (option.isInitSimpleAccordion) {
    initSimpleAccordion(selector);
  }
  if (option.isInitCarousel) {
    initCarousel(selector);
  }
  if (option.isInitItemCardHeight) {
    initItemcardheight(selector);
  }
  if (option.isInitDragHorizontalScroll) {
    initDragHorizontalScroll(selector);
  }
}

function reInitJsEvent(selectors: string[], option?: ReInitOption) {
  selectors.forEach((selector) => {
    if (option && !option.reInitAll) {
      initSeperatedOption(selector, option);
    } else if (option && option.reInitAll) {
      // init all
      initCheckBoxCtrl(selector);
      initWindowAddCart(selector);
      // IEのみ呼び出す
      if (isIe) {
        initIeStickyCol(selector);
      }
      // tooltipは必ずAddCartとIeStickyColした後より呼び出す
      initWindowTooltip(selector);
      initWindowDeleteBtn(selector);
      initModal(selector);
      initSimpleAccordion(selector);
      initCarousel(selector);
      initItemcardheight(selector);
      initDragHorizontalScroll(selector);
    }
    // else {} // no option, do nothing
  });
}

const ASYNC_CRITEO_ID = 'criteo-script';
const ASYNC_ANALYTICS_DATA_PAGE_ID = 'async-analytics-data-page';
const DEFAULT_JS_ENV_TAG_ID = 'js-env-id';
const NEW_MODEL_JS = 'TRYLION.Env.PAGE = ';
const ENDING_SEMICOLON = ';';
// 不具合修正(TRYLION_HC-1574)
// OneTagを再発火するたびにCRITEO広告が重複して生成される場合が存在するため、要素が存在する場合は削除を行う必要がある。
// OneTagを再発火する前に要素内を削除するdivタグ
const criteoDivTagIds = [
  // 広告掲載位置No.6 検索(キーワード検索結果画面) ページ上部 ディスプレイ（バナー＋商品）
  "criteo-search-top-cd",
  // 広告掲載位置No.9 検索(キーワード検索結果画面) ページ左側 ディスプレイ（バナー）
  "criteo-search-mid-left-cd",
  // 広告掲載位置No.11 検索(キーワード検索結果画面) ページ下側 ディスプレイ（バナー）
  "criteo-search-bottm-cd",
  // 広告掲載位置No.12 商品詳細 ページ中段 ディスプレイ（バナー＋商品）
  "criteo-pdp-mid-cd",
  // 広告掲載位置No.13 商品詳細 ページ中段 スポンサードプロダクト
  "criteo-pdp-mid-sp1",
  // 広告掲載位置No.14 商品詳細 ページ中段 スポンサードプロダクト
  "criteo-pdp-mid-sp2",
  // 広告掲載位置No.15 商品詳細 ページ下部 ディスプレイ（バナー）
  "criteo-pdp-bottom-cd",
  // 広告掲載位置No.16 大大・大カテゴリ リスト上部 ディスプレイ（バナー＋商品）
  "criteo-parentCategory-top-cd",
  // 広告掲載位置No.17 大大・大カテゴリ ページ中段 スポンサードプロダクト
  "criteo-parentCategory-bottm-sp",
  // 広告掲載位置No.18 大大・大カテゴリ ページ左側 ディスプレイ（バナー）
  "criteo-parentCategory-bottm-left-cd",
  // 広告掲載位置No.19 大大・大カテゴリ ページ下部 ディスプレイ（バナー）
  "criteo-parentCategory-bottm-cd",
  // 広告掲載位置No.20 中・小カテゴリ ページ上部 ディスプレイ（バナー＋商品）
  "criteo-childCategory-top-cd",
  // 広告掲載位置No.24 中・小カテゴリ ページ左側 ディスプレイ（バナー）
  "criteo-childCategory-mid-left-cd",
  // 広告掲載位置No.25 中・小カテゴリ ページ下部 ディスプレイ（バナー）
  "criteo-childCategory-bottm-cd",
  // 広告掲載位置No.26 バリエーション ページ上部 ディスプレイ（バナー＋商品）
  "criteo-variation-top-cd",
  // 広告掲載位置No.27 バリエーション ページ中段 スポンサードプロダクト
  "criteo-variation-mid-sp",
  // 広告掲載位置No.28 バリエーション ページ下部 ディスプレイ（バナー）
  "criteo-variation-bottom-cd"
];

const scrollIntoElement = (anchorId?: string) => {
  if (anchorId) {
    // 画面の他のJS優先初期化させるためにsetTimeout
    setTimeout(() => {
      const focusElement = document.querySelector(anchorId);
      if (focusElement) {
        // Jump to element
        // https://stackoverflow.com/a/13736194/1670643
        focusElement.scrollIntoView();
      } else {
        console.warn(`Focus id=${anchorId} not exist in loaded html`);
      }
    }, 0);
  }
};

const setNewPageAnalytics = (newDoc: Document) => {
  const newDocScript =
    (newDoc.getElementById(ASYNC_ANALYTICS_DATA_PAGE_ID) as HTMLScriptElement) || '';
  const prevScript =
    (document.getElementById(ASYNC_ANALYTICS_DATA_PAGE_ID) as HTMLScriptElement) || '';
  if (newDocScript && prevScript) {
    /// 非同期で読み込んだScriptタグを再度読み込ませる必要があるためdom生成をする
    const nextScript = document.createElement('script');
    nextScript.textContent = newDocScript.textContent;
    nextScript.type = 'text/javascript';
    nextScript.id = ASYNC_ANALYTICS_DATA_PAGE_ID;
    prevScript.replaceWith(nextScript);
  }
};

const execNewCriteoOneTag = (newDoc: Document) => {
  const newDocScript =
    (newDoc.getElementById(ASYNC_CRITEO_ID) as HTMLScriptElement) || '';
  const prevScript =
    (document.getElementById(ASYNC_CRITEO_ID) as HTMLScriptElement) || '';
  if (newDocScript && prevScript) {
    /// OneTagの再発火による広告の重複を避けるため要素が存在する場合は削除を行う
    for(const id of criteoDivTagIds) {
      let divTag = document.getElementById(id);
      if (divTag !== null) {
        divTag.innerHTML = ""
      }
    }
    /// 非同期で読み込んだScriptタグを再度読み込ませる必要があるためdom生成をする
    const nextScript = document.createElement('script');
    nextScript.textContent = newDocScript.textContent;
    nextScript.type = 'text/javascript';
    nextScript.id = ASYNC_CRITEO_ID;
    prevScript.replaceWith(nextScript);
  }
};

function callbackOnJsEnvLoaded<T>(newDoc: Document, option?: ReplaceOption<T>): void {
  try {
    if (option?.onJsEnvLoaded) {
      const newJsEnv = getJsEnv<T>(newDoc);
      if (newJsEnv) {
        option.onJsEnvLoaded(newJsEnv);
      }
    }
  } catch (e) {
    console.error('Invalid async script', e);
  }
}

function getJsEnv<T>(newDoc: Document): T | null {
  const scripts = newDoc.getElementById(DEFAULT_JS_ENV_TAG_ID)?.innerText || '';
  // eslint-disable-next-line
  if (scripts) {
    // 非同期で読み込んだJSデータを再度初期化する
    // eslint-disable-next-line
    for (const script of scripts.split(ENDING_SEMICOLON)) {
      // eslint-disable-next-line max-depth
      if (script.replace(/(^\s+)|(\s+$)/g,'').startsWith(NEW_MODEL_JS)) { // NOSONAR
        return JSON.parse(script.replace(NEW_MODEL_JS, ''))
      }
    }
  }
  return null;
}

function replaceInnerHTML<T>(selectors: string[], newDoc: Document, option?: ReplaceOption<T>) {
  let anySelectorMatch = false;
  // eslint-disable-next-line no-restricted-syntax
  for (const selector of selectors) {
    const froms = newDoc.querySelectorAll(selector);
    const tos = document.querySelectorAll(selector);
    if (froms.length && froms.length === tos.length) {
      anySelectorMatch = true;
      tos.forEach((to, i) => {
        // Only update when diff
        if (to.innerHTML !== froms[i].innerHTML) {
          to.innerHTML = froms[i].innerHTML;
        }
      });
    }
  }
  if (!anySelectorMatch && option?.onNoneSelectorMatch) {
    option.onNoneSelectorMatch();
  }
}

/**
 * プロモーション開発案件「カテゴリページの個別インデックス」で、
 * ページ遷移した際にcanonicalが変わらない、という不具合が発生（PROMOTION_SEO-61）。
 * 要素そのものを代替する対応が必要となった。
 * ReplaceSpecのReplaceModeを指定することで、置換方法を区別する対応をした。
 * 不具合対応による影響範囲を少なくするため、
 * 既存のreplaceInnerHTMLメソッドを元に本メソッドを新たに作成した。
 */
function replaceHTML<T>(replaceSpecs: ReplaceSpec[], newDoc: Document, option?: ReplaceOption<T>) {
  let anySelectorMatch = false;
  for (const replaceSpec of replaceSpecs) {
    const froms = newDoc.querySelectorAll(replaceSpec.selector);
    const tos = document.querySelectorAll(replaceSpec.selector);
    if (froms.length && froms.length === tos.length) {
      anySelectorMatch = true;
      tos.forEach((to, i) => {
        switch(replaceSpec.replaceMode) {
          case ReplaceMode.INSIDE_OF_TARGET:
            if (to.innerHTML !== froms[i].innerHTML) {
              to.innerHTML = froms[i].innerHTML;
            };
            break;
          case ReplaceMode.TARGET_ITSELF:
            to.replaceWith(froms[i]);
            break;
        }
      });
    }
  }
  if (!anySelectorMatch && option?.onNoneSelectorMatch) {
    option.onNoneSelectorMatch();
  }
}

/**
 * 新しいのDocumentより現在のDOMの一部（指定したselector）を代替する。
 * 自動的にLoadingのアニメーション付き、ロード完了後URLもJsで変わる
 *
 * @param newDoc 新しい画面のDocument例えば、loading_util.asyncPageのthenメソッドより受け取ったもの
 * @param selectors replace対象の画面箇所、新しい画面と現在の画面両方同じselector存在する必要
 * @param option 新しいDOMの引数定義、画面置換できない場合のアクション定義、置換後の画面遷移に必要
 * @see ReplaceOption
 */
const replace = <T>(newDoc: Document, selectors: string[], option?: ReplaceOption<T>): void => {
  scrollIntoElement(option?.scrollIntoAnchorSelector);
  // replace innerHTML
  replaceInnerHTML(selectors, newDoc, option);
  setNewPageAnalytics(newDoc);
  execNewCriteoOneTag(newDoc); // 絞り込み設定後のCRITEO OneTagをDOMに読み込んで再発火
  // setTimeout理由：UI表示優先、その後イベントを発火する
  setTimeout(() => {
    callbackOnJsEnvLoaded<T>(newDoc, option);
    reInitJsEvent(selectors, option);
  }, 0);
};

/**
 * プロモーション開発案件「カテゴリページの個別インデックス」で、
 * ページ遷移した際にcanonicalが変わらない、という不具合が発生（PROMOTION_SEO-61）。
 * ReplaceSpecを指定し、要素そのものを代替する対応をした。
 * 不具合対応による影響範囲を少なくするため、
 * 既存のreplaceメソッドを元に本メソッドを新たに作成した。
 *
 * @param newDoc 新しい画面のDocument例えば、loading_util.asyncPageのthenメソッドより受け取ったもの
 * @param replaceSpecs replace対象の画面箇所、新しい画面と現在の画面両方同じselector存在する必要
 * @param option 新しいDOMの引数定義、画面置換できない場合のアクション定義、置換後の画面遷移に必要
 * @see ReplaceOption
 */
const replaceByReplaceSpec = <T>(newDoc: Document, replaceSpecs: ReplaceSpec[], option?: ReplaceOption<T>): void => {
  scrollIntoElement(option?.scrollIntoAnchorSelector);
  replaceHTML(replaceSpecs, newDoc, option);
  setNewPageAnalytics(newDoc);
  execNewCriteoOneTag(newDoc); // 絞り込み設定後のCRITEO OneTagをDOMに読み込んで再発火
  // setTimeout理由：UI表示優先、その後イベントを発火する
  setTimeout(() => {
    callbackOnJsEnvLoaded<T>(newDoc, option);
    reInitJsEvent(replaceSpecs.map(it => it.selector), option);
  }, 0);
};

export const cancelLoading = (selector: string) => {
  loadingDisplayNone(selector);
  removeLoading(selector);
};

export const loadingDisplayNone = (selector: string) => {
  const element = document.getElementById(selector);
  if (element) {
    element.style.display = 'none';
  }
};

export const removeLoading = (selector: string) => {
  const element = document.getElementById(selector);
  if (element) {
    element.classList.remove('is-loading');
  }
};

export const setLoadingTimer = (selector: string) => {
  return setTimeout(cancelLoading, 30000, selector);
};

/**
 * 新しい画面に代替する際の置換方法を示す区分。
 * 要素内を置換する場合と、要素そのものを置換する場合に区別する。
 *
 * @param INSIDE_OF_TARGET 要素内を置換する
 * @param TARGET_ITSELF 要素そのものを置換する
 */
export enum ReplaceMode {
  INSIDE_OF_TARGET,
  TARGET_ITSELF,
}

export interface ReplaceSpec {
  selector: string;
  replaceMode: ReplaceMode;
}

const DomUtil = {
  replace,
  getJsEnv,
  replaceByReplaceSpec,
};

export default DomUtil;
